import { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { Header, Footer } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API_BASE_URL = "";
const API = `${API_BASE_URL}/api`;

const FORGOT_COPY = {
  fr: {
    title: "Mot de passe oublié",
    subtitle: "Entrez votre email pour réinitialiser",
    email: "Email",
    emailPlaceholder: "votre@email.com",
    loading: "Envoi...",
    submit: "Envoyer le lien",
    sentToast: "Email envoyé si le compte existe",
    errorToast: "Une erreur est survenue",
    sentMessage: "Si un compte existe avec cet email, vous recevrez un lien de réinitialisation.",
    backLogin: "Retour à la connexion",
  },
  en: {
    title: "Forgot password",
    subtitle: "Enter your email to reset it",
    email: "Email",
    emailPlaceholder: "your@email.com",
    loading: "Sending...",
    submit: "Send reset link",
    sentToast: "Email sent if the account exists",
    errorToast: "Something went wrong",
    sentMessage: "If an account exists with this email, you will receive a reset link.",
    backLogin: "Back to login",
  },
};

const ForgotPasswordPage = () => {
  const { language } = useLanguage();
  const copy = FORGOT_COPY[language] || FORGOT_COPY.fr;
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success(copy.sentToast);
    } catch {
      toast.error(copy.errorToast);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />

      <div className="min-h-screen flex items-center justify-center px-6 pt-20">
        <div className="w-full max-w-md">
          <div className="bg-[#18181B] border border-white/10 p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-black uppercase text-white mb-2">{copy.title}</h1>
              <p className="text-zinc-500">{copy.subtitle}</p>
            </div>

            {sent ? (
              <div className="text-center">
                <div className="w-16 h-16 bg-[#CCFF00]/10 mx-auto flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-[#CCFF00]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-zinc-400 mb-6">
                  {copy.sentMessage}
                </p>
                <Link
                  to="/login"
                  className="text-[#CCFF00] hover:underline"
                >
                  {copy.backLogin}
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="email" className="text-white text-sm font-medium">{copy.email}</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 bg-[#0A0A0A] border-white/20 text-white focus:border-[#CCFF00] focus:ring-1 focus:ring-[#CCFF00]"
                    placeholder={copy.emailPlaceholder}
                    data-testid="forgot-email"
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#CCFF00] text-black py-3 font-bold uppercase tracking-wide hover:bg-[#AACC00] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="forgot-submit"
                >
                  {loading ? copy.loading : copy.submit}
                </button>
              </form>
            )}

            <p className="text-center mt-6 text-zinc-500 text-sm">
              <Link to="/login" className="text-[#CCFF00] hover:underline">
                {copy.backLogin}
              </Link>
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ForgotPasswordPage;
